<template>
    <CRow alignVertical="center">
        <!-- Fields mapping -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Fields mapping") }}
            </h5>
            <CCTableAddItems
                :items.sync="settings.fieldsMapping"
                :fields="[
                    {
                        key: 'dataType',
                        label: 'Data type',
                    },
                    {
                        key: 'mappingName',
                        label: 'Mapping name',
                    },
                    {
                        key: '_toggled',
                        label: 'Toggled',
                        _active: false,
                        _defaultValue: false,
                    },
                    {
                        key: 'mappingValues',
                        label: 'Mapping values',
                        _active: false,
                        _defaultValue: {
                            values: [],
                            regexp: false,
                            defaultValue: null,
                        },
                    }
                ]"
                :showButtonToggleDetails="true"
                :normalizeItems="true"
            >
                <template #dataType="{ item }">
                    <SelectAsync
                        :value.sync="item.dataType"
                        :options="payload"
                        :placeholder="$t('Select a field')"
                        :invalidFeedback="$t('Field is requiered')"
                        size="sm"
                        class="mb-0"
                    />
                </template>
                <template #mappingName="{ item }">
                    <CInput
                        :value.sync="item.mappingName"
                        :placeholder="$t('Enter the value')"
                        :invalidFeedback="
                            $t('Value is required')
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
                <template #details="{ item }">
                    <CCollapse
                        :show="item._toggled"
                    >
                        <CContainer :fluid="true">
                            <CRow>
                                <CCol>
                                    <h5>
                                        {{ $t("Mapping Values") }}
                                    </h5>
                                    <p
                                        class="mb-0"
                                    >
                                        {{
                                            $t(
                                                "You can assign the value to sent."
                                            )
                                        }}.
                                    </p>
                                </CCol>
                            </CRow>
                            <CRow>
                                <CCol>
                                    <ButtonBulk
                                        :value.sync="item.mappingValues.values"
                                        keyLeftValue="inputValue"
                                        keyRightValue="outputValue"
                                    />
                                </CCol>
                            </CRow>
                            <CRow>
                                <CCol col="5">
                                    <CCSwitch
                                        :checked.sync="item.mappingValues.regexp"
                                        :horizontal="
                                            defaultSwitchConfig.horizontal
                                        "
                                        label="Regexp mapping"
                                        color="primary"
                                        shape="pill"
                                        variant="3d"
                                        size="sm"
                                    /> 
                                </CCol>
                                <CCol>
                                    <CInput
                                        :value.sync="item.mappingValues.defaultValue"
                                        :horizontal="
                                            defaultInputConfig.horizontal
                                        "
                                        label="Default value"
                                        :placeholder="
                                            $t(
                                                'Enter a default value'
                                            )
                                        "
                                        class="mb-0"
                                        size="sm"
                                    />
                                </CCol>
                            </CRow>
                            <CRow>
                                <CCol>
                                    <CCTableAddItems
                                        :items.sync="item.mappingValues.values"
                                        :fields="[
                                            {
                                                key: 'inputValue',
                                                label: 'Input value',
                                            },
                                            {
                                                key: 'outputValue',
                                                label: 'Output value',
                                            },
                                        ]"
                                    >
                                        <template #inputValue="{ index }">
                                            <CInput
                                                :value.sync="item.mappingValues.values[index].inputValue"
                                                :placeholder="$t('Enter the value')"
                                                :invalidFeedback="$t('Value is required')"
                                                class="mb-0"
                                                size="sm"
                                            />
                                        </template>
                                        <template #outputValue="{ index }">
                                            <CInput
                                                :value.sync="item.mappingValues.values[index].outputValue"
                                                :placeholder="$t('Enter the value')"
                                                :invalidFeedback="$t('Value is required')"
                                                class="mb-0"
                                                size="sm"
                                            />
                                        </template>
                                    </CCTableAddItems>
                                </CCol>
                            </CRow>
                        </CContainer>
                    </CCollapse>
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Fixed fields -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Fixed fields") }}
            </h5>
            <p class="mb-0">
                {{ $t("You can create fixed fields for your integration") }}.
            </p>
            <CCTableAddItems
                :items.sync="settings.fixedFields"
                :fields="[
                    {
                        key: 'name',
                        label: 'Field name',
                    },
                    {
                        key: 'value',
                        label: 'Field value',
                    },
                ]"
                noItemsViewText="Add parameter"
            >
                <template #name="{ item }">
                    <CInput
                        :value.sync="item.name"
                        :placeholder="$t('Enter the name')"
                        :invalidFeedback="
                            $t('Name is required')
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
                <template #value="{ item }">
                    <CInput
                        :value.sync="item.value"
                        :placeholder="$t('Enter the value')"
                        :invalidFeedback="
                            $t('Value is required')
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Match response OK in body -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{
                    $t(
                        "OK responses in body"
                    )
                }}
            </h5>
            <p class="mb-0">
                {{
                    $t(
                        "Set the conditions to search in the body to mark the response as ok"
                    )
                }}.
            </p>
            <CCTableAddItems
                :items.sync="
                    settings.matchResponseOKInBody
                "
                :fields="[
                    {
                        key: 'condition',
                        label: 'condition',
                    },
                    {
                        key: 'value',
                        label: 'value',
                    },
                ]"
            >
                <template
                    #condition="{
                        item,
                    }"
                >
                    <CSelect
                        :value.sync="
                            item.condition
                        "
                        :options="[
                            {
                                value: 'contains',
                                label: 'Contains',
                            },
                            {
                                value: 'not_contains',
                                label: 'Not contains',
                            },
                        ]"
                        :placeholder="
                            $t(
                                'Select a condition'
                            )
                        "
                        size="sm"
                    />
                </template>
                <template
                    #value="{
                        item,
                    }"
                >
                    <CInput
                        :value.sync="
                            item.value
                        "
                        :placeholder="
                            $t(
                                'Enter the value'
                            )
                        "
                        :invalidFeedback="
                            $t(
                                'Value is required'
                            )
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Match response DUP in body -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Match response DUP in body") }}
            </h5>
            <p class="mb-0">
                {{
                    $t(
                        "Set the conditions to search in the body to mark the response as duplicate"
                    )
                }}.
            </p>
            <CCTableAddItems
                :items.sync="
                    settings.matchResponseDUPInBody
                "
                :fields="[
                    {
                        key: 'condition',
                        label: 'condition',
                    },
                    {
                        key: 'value',
                        label: 'value',
                    },
                ]"
                
            >
                <template
                    #condition="{
                        item,
                    }"
                >
                    <CSelect
                        :value.sync="
                            item.condition
                        "
                        :options="[
                            {
                                value: 'contains',
                                label: 'Contains',
                            },
                            {
                                value: 'not_contains',
                                label: 'Not contains',
                            },
                        ]"
                        :placeholder="
                            $t(
                                'Select a condition'
                            )
                        "
                        size="sm"
                    />
                </template>
                <template
                    #value="{
                        item,
                    }"
                >
                    <CInput
                        :value.sync="
                            item.value
                        "
                        :placeholder="
                            $t(
                                'Enter the value'
                            )
                        "
                        :invalidFeedback="
                            $t(
                                'Value is required'
                            )
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Match response OK in headers -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Match response OK in headers") }}
            </h5>
            <p class="mb-0">
                {{
                    $t(
                        "Set the conditions to search in the headers to mark the response as ok"
                    )
                }}.
            </p>
            <CCTableAddItems
                :items.sync="
                    settings.matchResponseOKInHeaders
                "
                :fields="[
                    {
                        key: 'condition',
                        label: 'condition',
                    },
                    {
                        key: 'value',
                        label: 'value',
                    },
                ]"
            >
                <template
                    #condition="{
                        item,
                    }"
                >
                    <CSelect
                        :value.sync="
                            item.condition
                        "
                        :options="[
                            {
                                value: 'contains',
                                label: 'Contains',
                            },
                            {
                                value: 'not_contains',
                                label: 'Not contains',
                            },
                        ]"
                        :placeholder="
                            $t(
                                'Select a condition'
                            )
                        "
                        size="sm"
                    />
                </template>
                <template
                    #value="{
                        item,
                    }"
                >
                    <CInput
                        :value.sync="
                            item.value
                        "
                        :placeholder="
                            $t(
                                'Enter the value'
                            )
                        "
                        :invalidFeedback="
                            $t(
                                'Value is required'
                            )
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Match response DUP in headers -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Match response DUP in headers") }}
            </h5>
            <p class="mb-0">
                {{
                    $t(
                        "Set the conditions to search in the headers to mark the response as duplicate"
                    )
                }}.
            </p>
            <CCTableAddItems
                :items.sync="
                    settings.matchResponseDUPInHeaders
                "
                :fields="[
                    {
                        key: 'condition',
                        label: 'condition',
                    },
                    {
                        key: 'value',
                        label: 'value',
                    },
                ]"
            >
                <template
                    #condition="{
                        item,
                    }"
                >
                    <CSelect
                        :value.sync="
                            item.condition
                        "
                        :options="[
                            {
                                value: 'contains',
                                label: 'Contains',
                            },
                            {
                                value: 'not_contains',
                                label: 'Not contains',
                            },
                        ]"
                        :placeholder="
                            $t(
                                'Select a condition'
                            )
                        "
                        size="sm"
                    />
                </template>
                <template
                    #value="{
                        item,
                    }"
                >
                    <CInput
                        :value.sync="
                            item.value
                        "
                        :placeholder="
                            $t(
                                'Enter the value'
                            )
                        "
                        :invalidFeedback="
                            $t(
                                'Value is required'
                            )
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Match response OK in status line -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Match response OK in status line") }}
            </h5>
            <p class="mb-0">
                {{
                    $t(
                        "Set the conditions to search in the status line to mark the response as ok"
                    )
                }}.
            </p>
            <CCTableAddItems
                :items.sync="
                    settings.matchResponseOKInStatusLine
                "
                :fields="[
                    {
                        key: 'condition',
                        label: 'condition',
                    },
                    {
                        key: 'value',
                        label: 'value',
                    },
                ]"
            >
            <template
                    #condition="{
                        item,
                    }"
                >
                    <CSelect
                        :value.sync="
                            item.condition
                        "
                        :options="[
                            {
                                value: 'contains',
                                label: 'Contains',
                            },
                            {
                                value: 'not_contains',
                                label: 'Not contains',
                            },
                        ]"
                        :placeholder="
                            $t(
                                'Select a condition'
                            )
                        "
                        size="sm"
                    />
                </template>
                <template
                    #value="{
                        item,
                    }"
                >
                    <CInput
                        :value.sync="
                            item.value
                        "
                        :placeholder="
                            $t(
                                'Enter the value'
                            )
                        "
                        :invalidFeedback="
                            $t(
                                'Value is required'
                            )
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
        <!-- Match response DUP in status line -->
        <CCol md="12" sm="12">
            <h5 class="mt-3">
                {{ $t("Match response DUP in status line") }}
            </h5>
            <p class="mb-0">
                {{
                    $t(
                        "Set the conditions to search in the status line to mark the response as duplicate"
                    )
                }}.
            </p>
            <CCTableAddItems
                :items.sync="
                    settings.matchResponseDUPInStatusLine
                "
                :fields="[
                    {
                        key: 'condition',
                        label: 'condition',
                    },
                    {
                        key: 'value',
                        label: 'value',
                    },
                ]"
            >
            <template
                    #condition="{
                        item,
                    }"
                >
                    <CSelect
                        :value.sync="
                            item.condition
                        "
                        :options="[
                            {
                                value: 'contains',
                                label: 'Contains',
                            },
                            {
                                value: 'not_contains',
                                label: 'Not contains',
                            },
                        ]"
                        :placeholder="
                            $t(
                                'Select a condition'
                            )
                        "
                        size="sm"
                    />
                </template>
                <template
                    #value="{
                        item,
                    }"
                >
                    <CInput
                        :value.sync="
                            item.value
                        "
                        :placeholder="
                            $t(
                                'Enter the value'
                            )
                        "
                        :invalidFeedback="
                            $t(
                                'Value is required'
                            )
                        "
                        class="mb-0"
                        size="sm"
                    />
                </template>
            </CCTableAddItems>
        </CCol>
    </CRow>
</template>

<script>
import TableFixedFields from "~/components/TableFixedFields.vue";
import CCTableAddItems from "~/components/CCTableAddItems";
import CCSwitch from "~/components/CCSwitch";
import CustomTable from "~/components/CustomTable";
import ButtonAdd from "~/components/ButtonAdd";
import ButtonBulk from "~/components/ButtonBulk";
import SelectAsync from "£lement/SelectAsync";
import SelectTags from "£lement/SelectTags";

import tableMixin from "~/mixins/table.js";
import baseMixin from "~/mixins/base.js";

import {
    cilSave,
    cilArrowThickFromRight,
    cilSettings,
    cilPencil,
    cilTrash,
    cilPlus,
} from "@coreui/icons";

export default {
    name: "HttpRequestMatchingResponseActionSettings",
    mixins: [baseMixin, tableMixin],
    icons: {
        cilSave,
        cilArrowThickFromRight,
        cilSettings,
        cilPencil,
        cilTrash,
        cilPlus,
    },
    options: {},
    components: {
        TableFixedFields,
        CCTableAddItems,
        CCSwitch,
        CustomTable,
        SelectAsync,
        ButtonAdd,
        SelectTags,
        ButtonBulk,
    },
    data() {
        return {

            settings: {
                fixedFields: [],
                fieldsMapping: [],
                matchResponseOKInStatusLine: [],
                matchResponseDUPInStatusLine: [],
                matchResponseOKInHeaders: [],
                matchResponseDUPInHeaders: [],
                matchResponseOKInBody: [],
                matchResponseDUPInBody: [],

            }
        };
    },
    props: {
        payload: {
            required: true,
        },
        value: {
            required: true,
        },
    },
    watch: {
        value(newValue) {
            this.settings.fixedFields = newValue.fixedFields || [];
            this.settings.fieldsMapping = newValue.fieldsMapping || [];
            this.settings.matchResponseOKInStatusLine = newValue.matchResponseOKInStatusLine || [];
            this.settings.matchResponseDUPInStatusLine = newValue.matchResponseDUPInStatusLine || [];
            this.settings.matchResponseOKInHeaders = newValue.matchResponseOKInHeaders || [];
            this.settings.matchResponseDUPInHeaders = newValue.matchResponseDUPInHeaders || [];
            this.settings.matchResponseOKInBody = newValue.matchResponseOKInBody || [];
            this.settings.matchResponseDUPInBody = newValue.matchResponseDUPInBody || [];
        },
        settings: {
            handler(newValue) {
                this.$emit("update:value", newValue);
                this.$emit("change", newValue);
            },
            deep: true,
        },
    },
    async mounted() {
        this.settings.fixedFields = this.value.fixedFields || [];
        this.settings.fieldsMapping = this.value.fieldsMapping || [];
        this.settings.matchResponseOKInStatusLine = this.value.matchResponseOKInStatusLine || [];
        this.settings.matchResponseDUPInStatusLine = this.value.matchResponseDUPInStatusLine || [];
        this.settings.matchResponseOKInHeaders = this.value.matchResponseOKInHeaders || [];
        this.settings.matchResponseDUPInHeaders = this.value.matchResponseDUPInHeaders || [];
        this.settings.matchResponseOKInBody = this.value.matchResponseOKInBody || [];
        this.settings.matchResponseDUPInBody = this.value.matchResponseDUPInBody || [];
    },
};
</script>

<style>
.range__not_defined {
    accent-color: gray;
}
</style>
